import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { BookingStatus, ErrorCode } from '../../../__generated__/graphql';
import { Button } from '../../components/button';
import { Modal } from '../../components/modal';
import {
  Alert,
  Card,
  CardBody,
  LoadingBlock,
  Muted,
  PageContainer,
  PageHeader,
  SectionHeader,
  Stack,
  Strong,
} from '../../components/ui';
import {
  useAcceptBookingRequestMutation,
  useDeclineBookingRequestMutation,
  useHostTodayQuery,
  type IStayBookingFieldsFragment,
} from '../../services/stayService/__generated__/queries.generated';
import { BookingRow } from '../StayBookingPage/StayBookingLists';
import {
  TODAY_SECTIONS,
  todaySummary,
  type TodaySectionKey,
} from '../StayBookingPage/stayLifecycle';
import { firstGraphQLError, stayErrorMessage } from './stayErrors';

/**
 * The host's landing on the stays site (spec 016 W23): requests to answer, today's
 * checkouts and arrivals, guests staying now, what is coming, and reviews to write.
 */
export default function TodayPage() {
  const navigate = useNavigate();
  const { data, loading, error, refetch } = useHostTodayQuery({
    fetchPolicy: 'cache-and-network',
    pollInterval: 5 * 60 * 1000,
  });
  const today = data?.hostToday;
  const counts = Object.fromEntries(
    TODAY_SECTIONS.map(section => [section.key, today?.[section.key]?.length ?? 0]),
  ) as Record<TodaySectionKey, number>;

  return (
    <PageContainer width="lg" canvas>
      <PageHeader
        eyebrow="Hosting"
        title="Today"
        description={today ? todaySummary(counts) : undefined}
        actions={
          <Button
            theme="secondary"
            size="sm"
            text="Reservations"
            onClick={() => navigate('/stays/host/reservations')}
          />
        }
      />
      {error && !today ? (
        <Alert tone="error" title="Could not load">
          {stayErrorMessage(error)}
        </Alert>
      ) : null}
      {loading && !today ? <LoadingBlock label="Loading today" /> : null}
      {today ? (
        <Stack $gap={8} data-testid="stay-today">
          {TODAY_SECTIONS.map(section => {
            const rows = today[section.key] ?? [];
            // Upcoming and reviews stay quiet when empty; the rest say so.
            if (!rows.length && (section.key === 'upcoming' || section.key === 'pendingReviews')) {
              return null;
            }
            return (
              <Stack $gap={4} key={section.key} data-testid={`stay-today-${section.key}`}>
                <SectionHeader
                  title={`${section.title}${rows.length ? ` (${rows.length})` : ''}`}
                />
                {rows.length === 0 ? (
                  <Muted>{section.empty}</Muted>
                ) : section.key === 'pendingRequests' ? (
                  rows.map(booking => (
                    <RequestCard key={booking.id} booking={booking} onAnswered={() => refetch()} />
                  ))
                ) : (
                  rows.map(booking => (
                    <BookingRow
                      key={booking.id}
                      booking={booking}
                      viewer="host"
                      onOpen={() => navigate(`/stays/host/booking/${booking.id}`)}
                    />
                  ))
                )}
              </Stack>
            );
          })}
          <Muted $size="sm">
            <Link to="/stays/host/listings">Your listings</Link> ·{' '}
            <Link to="/stays/host/reviews">Reviews</Link> ·{' '}
            <Link to="/stays/host/claims">Resolution Center</Link>
          </Muted>
        </Stack>
      ) : null}
    </PageContainer>
  );
}

/** A request with its message and Accept / Decline right on the Today page. */
function RequestCard({
  booking,
  onAnswered,
}: {
  booking: IStayBookingFieldsFragment;
  onAnswered: () => void;
}) {
  const navigate = useNavigate();
  const [declineOpen, setDeclineOpen] = useState(false);
  const [accept, { loading: accepting }] = useAcceptBookingRequestMutation();
  const [decline, { loading: declining }] = useDeclineBookingRequestMutation();
  const guestName = booking.guest?.firstName || booking.guest?.displayName || 'A guest';

  const acceptRequest = async () => {
    try {
      const { data } = await accept({ variables: { bookingId: booking.id } });
      if (data?.acceptBookingRequest.status === BookingStatus.Booked) {
        toast.success(`${guestName}'s stay is booked.`);
      } else {
        toast.error('The guest’s card was declined, so the stay was not booked.');
      }
      onAnswered();
    } catch (e) {
      const code = firstGraphQLError(e)?.extensions?.code;
      toast.error(stayErrorMessage(e, 'The request could not be accepted'), { autoClose: 8000 });
      if (code === ErrorCode.RequestConflict || code === ErrorCode.RequestExpired) onAnswered();
    }
  };

  const declineRequest = async () => {
    try {
      await decline({ variables: { bookingId: booking.id, reason: null } });
      setDeclineOpen(false);
      toast.success('Request declined. The hold on the guest’s card was released.');
      onAnswered();
    } catch (e) {
      toast.error(stayErrorMessage(e, 'The request could not be declined'));
    }
  };

  return (
    <Card data-testid="stay-today-request">
      <CardBody>
        <Stack $gap={4}>
          <BookingRow
            booking={booking}
            viewer="host"
            onOpen={() => navigate(`/stays/host/booking/${booking.id}`)}
          />
          <Muted>{booking.requestMessage ? `“${booking.requestMessage}”` : 'No message.'}</Muted>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button
              text="Accept"
              isLoading={accepting}
              onClick={acceptRequest}
              dataTestId="stay-today-accept"
            />
            <Button
              theme="outlined"
              text="Decline"
              onClick={() => setDeclineOpen(true)}
              dataTestId="stay-today-decline"
            />
          </div>
        </Stack>
      </CardBody>
      <Modal
        isOpen={declineOpen}
        onClose={() => setDeclineOpen(false)}
        title="Decline request"
        modalWidth="420px"
      >
        <Stack $gap={4}>
          <Strong style={{ fontSize: 20 }}>Decline {guestName}'s request?</Strong>
          <Muted>The hold on the guest’s card is released right away.</Muted>
          <Button
            theme="danger"
            text="Decline"
            fullWidth
            isLoading={declining}
            onClick={declineRequest}
            dataTestId="stay-today-decline-confirm"
          />
        </Stack>
      </Modal>
    </Card>
  );
}
